import { create } from 'zustand';
import { CameraState, CanvasLayoutMode } from '../types/canvas';

export interface CanvasStoreState {
  layoutMode: CanvasLayoutMode;
  treeOrientation: 'vertical' | 'horizontal';
  camera: CameraState;
  containerSize: { width: number; height: number };
  selectedNodeId: string | null;
  searchQuery: string;
  highlightedPathNodeIds: string[];
  hideDormant: boolean;
  isFullscreen: boolean;

  // Actions
  setLayoutMode: (mode: CanvasLayoutMode) => void;
  setTreeOrientation: (orientation: 'vertical' | 'horizontal') => void;
  setCamera: (camera: Partial<CameraState>) => void;
  setContainerSize: (size: { width: number; height: number }) => void;
  resetCamera: (
    containerWidth?: number,
    containerHeight?: number,
    bounds?: { minX: number; maxX: number; minY: number; maxY: number }
  ) => void;
  setSelectedNodeId: (id: string | null) => void;
  setSearchQuery: (query: string) => void;
  setHighlightedPath: (path: string[]) => void;
  setHideDormant: (hide: boolean) => void;
  toggleHideDormant: () => void;
  setIsFullscreen: (isFullscreen: boolean) => void;
  zoomIn: (factor?: number) => void;
  zoomOut: (factor?: number) => void;
  fitToScreen: (
    containerWidth?: number,
    containerHeight?: number,
    bounds?: { minX: number; maxX: number; minY: number; maxY: number }
  ) => void;
}

const DEFAULT_CAMERA: CameraState = {
  x: 0,
  y: 0,
  zoom: 1.0,
};

export const useCanvasStore = create<CanvasStoreState>((set, get) => ({
  layoutMode: 'radial',
  treeOrientation: 'vertical',
  camera: DEFAULT_CAMERA,
  containerSize: { width: 0, height: 0 },
  selectedNodeId: null,
  searchQuery: '',
  highlightedPathNodeIds: [],
  hideDormant: false,
  isFullscreen: false,

  setLayoutMode: (mode: CanvasLayoutMode) => set({ layoutMode: mode }),

  setTreeOrientation: (orientation: 'vertical' | 'horizontal') => set({ treeOrientation: orientation }),

  setCamera: (cameraUpdate: Partial<CameraState>) =>
    set((state) => ({
      camera: { ...state.camera, ...cameraUpdate },
    })),

  setContainerSize: (size: { width: number; height: number }) => set({ containerSize: size }),

  resetCamera: (
    containerWidth?: number,
    containerHeight?: number,
    bounds?: { minX: number; maxX: number; minY: number; maxY: number }
  ) => {
    const w = containerWidth || get().containerSize.width || (typeof window !== 'undefined' ? window.innerWidth : 800);
    const h = containerHeight || get().containerSize.height || (typeof window !== 'undefined' ? window.innerHeight : 600);
    if (!bounds || w <= 0 || h <= 0) {
      set({ camera: DEFAULT_CAMERA });
      return;
    }
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;
    set({
      camera: {
        x: w / 2 - centerX,
        y: h / 2 - centerY,
        zoom: 1.0,
      },
    });
  },

  setSelectedNodeId: (id: string | null) => set({ selectedNodeId: id }),

  setSearchQuery: (query: string) => set({ searchQuery: query }),

  setHighlightedPath: (path: string[]) => set({ highlightedPathNodeIds: path }),

  setHideDormant: (hide: boolean) => set({ hideDormant: hide }),

  toggleHideDormant: () => set((state) => ({ hideDormant: !state.hideDormant })),

  setIsFullscreen: (isFullscreen: boolean) => set({ isFullscreen }),

  zoomIn: (factor = 1.2) => {
    const current = get().camera;
    const newZoom = Math.min(3.0, current.zoom * factor);
    set({ camera: { ...current, zoom: newZoom } });
  },

  zoomOut: (factor = 0.8) => {
    const current = get().camera;
    const newZoom = Math.max(0.15, current.zoom * factor);
    set({ camera: { ...current, zoom: newZoom } });
  },

  fitToScreen: (
    containerWidth?: number,
    containerHeight?: number,
    bounds?: { minX: number; maxX: number; minY: number; maxY: number }
  ) => {
    if (!bounds) return;
    const w = containerWidth || get().containerSize.width || (typeof window !== 'undefined' ? window.innerWidth : 800);
    const h = containerHeight || get().containerSize.height || (typeof window !== 'undefined' ? window.innerHeight : 600);
    if (w <= 0 || h <= 0) return;

    const padding = 80;
    const contentWidth = Math.max(100, bounds.maxX - bounds.minX + padding * 2);
    const contentHeight = Math.max(100, bounds.maxY - bounds.minY + padding * 2);

    const scaleX = w / contentWidth;
    const scaleY = h / contentHeight;
    const fitScale = Math.max(0.2, Math.min(1.2, Math.min(scaleX, scaleY)));

    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    const tx = w / 2 - centerX * fitScale;
    const ty = h / 2 - centerY * fitScale;

    set({
      camera: {
        x: tx,
        y: ty,
        zoom: fitScale,
      },
    });
  },
}));
